import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { Button } from "ui/button";

import Icons from "assets";

interface BorderRadiusCardProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: ((borderValues: string) => void) | (() => Promise<void>);
  headline?: string;
  error?: string;
}

const BorderRadiusCard = ({
  isOpen,
  onClose,
  onConfirm,
  headline = "Border radius",
  error,
}: BorderRadiusCardProps) => {
  // form
  const { getValues } = useFormContext();

  // custom border object
  const [customBorderVals, setCustomBorderVals] = useState({
    topLeft: "0",
    topRight: "0",
    bottomRight: "0",
    bottomLeft: "0",
  });

  const handleConfirm = async () => {
    const { topLeft, topRight, bottomRight, bottomLeft } = customBorderVals;
    const borderValues = `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`;

    try {
      await onConfirm(borderValues);
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  const handleBlur = (key: keyof typeof customBorderVals) => {
    setCustomBorderVals((prev) => ({
      ...prev,
      [key]: prev[key] === "" ? "0" : prev[key],
    }));
  };

  // handle change for border inputs with restrictions on characters allowed
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, key: string) => {
    let value = event.target.value;

    // remove non-numeric characters
    value = value.replace(/\D/g, "");

    // removing leading 0
    value = value.replace(/^0+(?=\d)/, "");

    // controlling max length to make sure user cannnot trigger error
    if (value.length > 4) {
      value = value.slice(0, 4);
    }

    setCustomBorderVals((prev) => ({ ...prev, [key]: value }));
  };

  // set input values on mount
  useEffect(() => {
    //HIDDEN till v2
    // const defaultBorderVals = THEME_DEFAULT_DATA.button.borderRadius.custom;

    // getting the current custom border values
    const defaultBorderVals = "4px 4px 4px 4px";

    const currentBorderVals = getValues("button.borderRadius.custom") || defaultBorderVals;

    // split string by spaces and remove 'px' from each value
    const [topLeft = "0", topRight = "0", bottomRight = "0", bottomLeft = "0"] = currentBorderVals
      .split(" ")
      .map((val: string) => val.replace("px", ""));

    setCustomBorderVals({ topLeft, topRight, bottomRight, bottomLeft });

    // eslint-disable-next-line
  }, [isOpen]);

  const inputStyles = twMerge(
    "px-2 py-2 bg-neutrals-5 border border-neutrals-4 rounded text-center w-14 h-7 outline-none",
    "transition-all hover:ring-1 hover:ring-teal-700 focus:ring-1 focus:ring-teal-700 focus:shadow-input",
  );

  if (!isOpen) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mt-2">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{headline}</h3>
        <button type="button" onClick={onClose}>
          <Icons.Close className="size-3" />
        </button>
      </div>
      <div className="mt-4 flex items-center gap-4">
        <div className="flex flex-col gap-2">
          <input
            type="text"
            className={inputStyles}
            value={customBorderVals.topLeft}
            onBlur={() => handleBlur("topLeft")}
            onChange={(e) => handleChange(e, "topLeft")}
          />
          <input
            type="text"
            className={inputStyles}
            value={customBorderVals.bottomLeft}
            onBlur={() => handleBlur("bottomLeft")}
            onChange={(e) => handleChange(e, "bottomLeft")}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Icons.BorderPiece />
          <Icons.BorderPiece className="rotate-90" />
          <Icons.BorderPiece className="-rotate-90" />
          <Icons.BorderPiece className="rotate-180" />
        </div>
        <div className="flex flex-col gap-2 pb-2">
          <input
            type="text"
            className={inputStyles}
            value={customBorderVals.topRight}
            onBlur={() => handleBlur("topRight")}
            onChange={(e) => handleChange(e, "topRight")}
          />
          <input
            type="text"
            className={inputStyles}
            value={customBorderVals.bottomRight}
            onBlur={() => handleBlur("bottomRight")}
            onChange={(e) => handleChange(e, "bottomRight")}
          />
        </div>
      </div>
      {error && <p className="text-red-500 text-sm mt-3">{error}</p>}
      <div className="mt-6 flex justify-between">
        <Button variant="outlined" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button size="small" onClick={handleConfirm}>
          Submit
        </Button>
      </div>
    </div>
  );
};

export default BorderRadiusCard;
